/**
 * Convert Arabic-Indic (٠-٩) and Persian (۰-۹) numerals to Latin/ASCII digits (0-9).
 * Leaves all other characters untouched.
 *
 * @param {string} str
 * @returns {string}
 */
export function toLatinDigits(str) {
  if (typeof str !== 'string') return str;
  return str.replace(/[\u0660-\u0669\u06F0-\u06F9]/g, (char) => {
    const code = char.charCodeAt(0);
    if (code >= 0x0660 && code <= 0x0669) {
      return String(code - 0x0660);
    }
    if (code >= 0x06f0 && code <= 0x06f9) {
      return String(code - 0x06f0);
    }
    return char;
  });
}

/**
 * Install a capture-phase `input` listener on `doc` that rewrites Arabic and Persian
 * digits to Latin/ASCII digits for INPUT (except type="password") and TEXTAREA targets.
 * Restores selectionStart / selectionEnd when supported by the input element.
 *
 * @param {Document} [doc=document]
 * @returns {Function} cleanup function to remove the listener
 */
export function installLatinDigitInput(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || typeof doc.addEventListener !== 'function') {
    return () => {};
  }

  const onInput = (event) => {
    const target = event.target;
    if (!target) return;

    const tagName = target.tagName;
    if (tagName !== 'INPUT' && tagName !== 'TEXTAREA') return;
    if (tagName === 'INPUT' && target.type === 'password') return;

    const value = target.value;
    if (typeof value !== 'string') return;

    if (/[\u0660-\u0669\u06F0-\u06F9]/.test(value)) {
      let canSelect = false;
      let start = null;
      let end = null;
      try {
        start = target.selectionStart;
        end = target.selectionEnd;
        canSelect = typeof start === 'number' && typeof end === 'number';
      } catch {
        canSelect = false;
      }

      const nextValue = toLatinDigits(value);
      const proto =
        tagName === 'INPUT'
          ? (typeof window !== 'undefined' && window.HTMLInputElement?.prototype) || null
          : (typeof window !== 'undefined' && window.HTMLTextAreaElement?.prototype) || null;
      const nativeSetter = proto ? Object.getOwnPropertyDescriptor(proto, 'value')?.set : null;

      if (nativeSetter) {
        nativeSetter.call(target, nextValue);
      } else {
        target.value = nextValue;
      }

      if (canSelect && typeof target.setSelectionRange === 'function') {
        try {
          target.setSelectionRange(start, end);
        } catch {
          // ignore if setSelectionRange is not allowed for this input type
        }
      }
    }
  };

  doc.addEventListener('input', onInput, true);

  return () => {
    doc.removeEventListener('input', onInput, true);
  };
}
